import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { agoraISO, fmtBrl, hojeISO, margemProduto, uid } from '../../utils/barbeariaLogica';
import type { BarbeariaEstado, TipoDespesa } from '../../utils/barbeariaTipos';

interface Props {
  estado: BarbeariaEstado;
  patch: (fn: (prev: BarbeariaEstado) => BarbeariaEstado) => void;
}

export default function BarbeariaGastos({ estado, patch }: Props) {
  const [despesa, setDespesa] = useState({
    tipo: 'variavel' as TipoDespesa,
    descricao: '',
    valor: '',
    dataISO: hojeISO(),
    quantidade: '1',
  });
  const [produto, setProduto] = useState({
    nome: '',
    qtdAtual: '',
    precoCusto: '',
    precoVenda: '',
  });

  const addDespesa = () => {
    const valor = Number(String(despesa.valor).replace(',', '.'));
    if (!despesa.descricao.trim() || !Number.isFinite(valor) || valor <= 0) return;
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      despesas: [
        {
          id: uid('dsp'),
          tipo: despesa.tipo,
          descricao: despesa.descricao.trim(),
          valor,
          dataISO: despesa.dataISO,
          quantidade: Number(despesa.quantidade) || 1,
          criadoEm: agora,
        },
        ...prev.despesas,
      ],
      atualizadoEm: agora,
    }));
    setDespesa((d) => ({ ...d, descricao: '', valor: '' }));
  };

  const remDespesa = (id: string) => {
    patch((prev) => ({
      ...prev,
      despesas: prev.despesas.filter((d) => d.id !== id),
      atualizadoEm: agoraISO(),
    }));
  };

  const addProduto = () => {
    if (!produto.nome.trim()) return;
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      estoque: [
        ...prev.estoque,
        {
          id: uid('stk'),
          nome: produto.nome.trim(),
          qtdAtual: Number(produto.qtdAtual) || 0,
          precoCusto: Number(String(produto.precoCusto).replace(',', '.')) || 0,
          precoVenda: Number(String(produto.precoVenda).replace(',', '.')) || 0,
          atualizadoEm: agora,
        },
      ],
      atualizadoEm: agora,
    }));
    setProduto({ nome: '', qtdAtual: '', precoCusto: '', precoVenda: '' });
  };

  const atualizarQtd = (id: string, qtdAtual: number) => {
    patch((prev) => ({
      ...prev,
      estoque: prev.estoque.map((p) =>
        p.id === id ? { ...p, qtdAtual, atualizadoEm: agoraISO() } : p
      ),
      atualizadoEm: agoraISO(),
    }));
  };

  const remProduto = (id: string) => {
    patch((prev) => ({
      ...prev,
      estoque: prev.estoque.filter((p) => p.id !== id),
      atualizadoEm: agoraISO(),
    }));
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-tour="gas-painel">
      <section className="bb-card">
        <h3 className="mb-2 text-sm font-bold uppercase text-neutral-800">
          Fluxo de caixa — despesas
        </h3>
        <p className="mb-3 text-xs text-neutral-500">
          Fixos: aluguel, luz, internet. Variáveis: lâmina, gola, pomada, shampoo por atendimento.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            Tipo
            <select
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={despesa.tipo}
              onChange={(e) =>
                setDespesa((d) => ({ ...d, tipo: e.target.value as TipoDespesa }))
              }
            >
              <option value="fixo">Custo fixo</option>
              <option value="variavel">Custo variável</option>
            </select>
          </label>
          <label className="text-xs">
            Data
            <input
              type="date"
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={despesa.dataISO}
              onChange={(e) => setDespesa((d) => ({ ...d, dataISO: e.target.value }))}
            />
          </label>
          <label className="col-span-2 text-xs">
            Descrição
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={despesa.descricao}
              onChange={(e) => setDespesa((d) => ({ ...d, descricao: e.target.value }))}
              placeholder="Ex: Lâminas de navalha"
            />
          </label>
          <label className="text-xs">
            Valor (R$)
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={despesa.valor}
              onChange={(e) => setDespesa((d) => ({ ...d, valor: e.target.value }))}
            />
          </label>
          <label className="text-xs">
            Qtd
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={despesa.quantidade}
              onChange={(e) => setDespesa((d) => ({ ...d, quantidade: e.target.value }))}
            />
          </label>
        </div>
        <button
          type="button"
          onClick={addDespesa}
          className="bb-btn-primary mt-3"
        >
          <Plus className="h-3.5 w-3.5" /> Lançar despesa
        </button>

        <ul className="mt-4 max-h-64 space-y-1 overflow-y-auto text-sm">
          {estado.despesas.slice(0, 40).map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-neutral-100 px-2 py-1.5"
            >
              <div>
                <span
                  className={`mr-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                    d.tipo === 'fixo'
                      ? 'bg-neutral-200 text-neutral-800'
                      : 'bg-amber-100 text-amber-900'
                  }`}
                >
                  {d.tipo}
                </span>
                {d.descricao}
                <div className="text-[11px] text-neutral-500">
                  {d.dataISO} · {fmtBrl(d.valor)}
                </div>
              </div>
              <button type="button" onClick={() => remDespesa(d.id)} className="text-red-600">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="bb-card">
        <h3 className="mb-2 text-sm font-bold uppercase text-neutral-800">
          Estoque / revenda
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <label className="col-span-2 text-xs">
            Produto
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={produto.nome}
              onChange={(e) => setProduto((p) => ({ ...p, nome: e.target.value }))}
            />
          </label>
          <label className="text-xs">
            Qtd atual
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={produto.qtdAtual}
              onChange={(e) => setProduto((p) => ({ ...p, qtdAtual: e.target.value }))}
            />
          </label>
          <label className="text-xs">
            Custo
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={produto.precoCusto}
              onChange={(e) => setProduto((p) => ({ ...p, precoCusto: e.target.value }))}
            />
          </label>
          <label className="col-span-2 text-xs">
            Preço de venda
            <input
              className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              value={produto.precoVenda}
              onChange={(e) => setProduto((p) => ({ ...p, precoVenda: e.target.value }))}
            />
          </label>
        </div>
        <button
          type="button"
          onClick={addProduto}
          className="bb-btn-dark mt-3"
        >
          <Plus className="h-3.5 w-3.5" /> Adicionar produto
        </button>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-100 text-xs uppercase text-neutral-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Produto</th>
                <th className="px-2 py-1.5">Qtd</th>
                <th className="px-2 py-1.5">Custo</th>
                <th className="px-2 py-1.5">Venda</th>
                <th className="px-2 py-1.5">Margem</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {estado.estoque.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-2 py-1.5 font-medium">{p.nome}</td>
                  <td className="px-2 py-1.5 text-center">
                    <input
                      type="number"
                      className="w-16 rounded border px-1 py-0.5 text-center text-xs"
                      value={p.qtdAtual}
                      onChange={(e) => atualizarQtd(p.id, Number(e.target.value) || 0)}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center text-xs">{fmtBrl(p.precoCusto)}</td>
                  <td className="px-2 py-1.5 text-center text-xs">{fmtBrl(p.precoVenda)}</td>
                  <td className="px-2 py-1.5 text-center text-xs font-semibold">
                    {margemProduto(p).toFixed(1)}%
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <button type="button" onClick={() => remProduto(p.id)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
